import {
  API_KEY_MESSAGE_TYPE,
  MAX_FONT_FAMILY_LENGTH,
  THEME_MESSAGE_TYPE,
  VIVIAN_EVENTS,
  evaluateContrast,
  parseCssLength,
  pickOnColor,
} from "@vivian/shared-contracts";
import type { ThemeTokens } from "@vivian/shared-contracts";
import { createMessageRelay } from "./message-relay";

const DEFAULT_EMBED_URL = "https://embed.vivian.app";
// Fase 5 redesign (08/2026): bumped from 640px — real-pilot feedback
// ("informações bagunçadas no quadro abaixo do vídeo") drove a two-column
// layout (see embed-app's TryOnStage.tsx) that needs a bit more vertical
// budget than the old single-column stack did.
const DEFAULT_IFRAME_HEIGHT = "700px";
const DEFAULT_HOST_MAX_WIDTH = "720px";
// F7.1 (tema do lojista, entrega 1): the two values that used to be
// hardcoded inline styles with no way for the host to override them at all
// — see `openTryOn()`'s own comment on why they're inline styles in the
// first place (the Fase 1 pilot's squeezed-iframe bug) for why these still
// end up as inline styles too, just READ from a custom property now instead
// of being literals.
const DEFAULT_HOST_WIDTH = "100%";
const DEFAULT_IFRAME_RADIUS = "1rem";
// F7.1 (tema do lojista, entrega 2): the button's own stylesheet default —
// see `renderTrigger()`'s `<style>` block, which keeps this EXACT literal
// hardcoded as the safe fallback rather than a bare `var(--vivian-color-primary,
// #6b21ff)`. Kept as a named constant here too so the accessibility-gate
// comparison against it (in tests) and the value itself can't drift apart.
const DEFAULT_COLOR_PRIMARY = "#6b21ff";
// The embed app's own background is fixed (`color-scheme: light`,
// `background: #ffffff` in `embed-app/index.html`) — see that file's own
// comment on why it never depends on the host page's theme. The LOADER's
// own document has no such guarantee (the trigger button renders directly
// in the optician's page, whose background is unknowable) — but the button
// ALWAYS pairs its background with hardcoded white text (`color: #fff`), so
// the one contrast pair that actually matters here is (chosen background,
// white text), same math either way since `contrastRatio` is symmetric.
const WHITE = "#ffffff";

// Fix critico (revisao final de deploy/vps-hostinger): capturado no CARREGAMENTO
// do modulo (nao dentro de um callback — `document.currentScript` so existe
// durante a execucao sincrona do <script> classico que carregou este arquivo;
// dentro de qualquer callback assincrono ele ja voltou a `null`). E' a origem
// de onde o PRORIO loader.js foi servido — hoje
// "https://vivian.truecolorsvision.com.br", amanha possivelmente outro dominio.
const LOADER_ORIGIN: string | null = (() => {
  try {
    const currentScript = document.currentScript as HTMLScriptElement | null;
    return currentScript?.src ? new URL(currentScript.src, document.baseURI).origin : null;
  } catch {
    return null;
  }
})();

// `embed-url` is an attribute the host page controls. Before we hand it an
// <iframe> with allow="camera" + allow-scripts/allow-same-origin, restrict
// it to origins we trust: any https://*.vivian.app host, http(s)://localhost
// for local Phase 1 development against a local embed-app dev server, OR the
// exact https origin the loader script ITSELF was served from.
//
// Essa terceira regra e' o fix critico: em producao o embed-url do demo e da
// loja TrueColors e' "https://vivian.truecolorsvision.com.br/...", que nao e'
// nem localhost nem *.vivian.app — o guard original rejeitava e o try-on
// nunca abria (achado na revisao final da branch deploy/vps-hostinger; os
// curls do item 12 do checklist nao enxergam essa falha porque ela so
// acontece do lado do navegador). Confiar na origem do proprio loader nao
// concede privilegio novo: quem hospeda loader.js ja executa JavaScript
// arbitrario na pagina do lojista, entao essa origem ja e' tao confiavel
// quanto o dominio de deploy em si — e isso torna o guard independente do
// dominio de implantacao (hoje truecolorsvision.com.br, amanha outro) sem
// precisar de uma lista fixa por tenant. `loaderOrigin` e' um parametro (com
// default `LOADER_ORIGIN`, o valor real capturado acima) para que este guard
// continue uma funcao pura e testavel sem depender de `document.currentScript`
// estar presente no ambiente de teste.
export function isAllowedEmbedOrigin(url: URL, loaderOrigin: string | null = LOADER_ORIGIN): boolean {
  if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
    return true;
  }
  if (url.protocol === "https:" && /(^|\.)vivian\.app$/i.test(url.hostname)) {
    return true;
  }
  return loaderOrigin !== null && url.protocol === "https:" && url.origin === loaderOrigin;
}

// `api-url` is likewise host-page-controlled, and its value ends up driving
// fetch()/<img src> targets executed inside the iframe (devsecops-validator
// review, MÉDIO-1) — unlike embed-url it can legitimately point at any
// tenant-owned API host, so this only enforces the scheme: https in
// production, or http(s)://localhost for local Phase 1 development.
function isAllowedApiOrigin(url: URL): boolean {
  if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
    return true;
  }
  return url.protocol === "https:";
}

/**
 * `<use-vivian>` — the custom element opticians embed on their site.
 *
 * Phase 0 skeleton: renders a placeholder trigger button in a shadow root
 * and lazily mounts an iframe pointing at the embed app on click. Message
 * relaying between the iframe and the host page is handled by
 * {@link createMessageRelay}.
 */
export class UseVivianElement extends HTMLElement {
  private readonly shadow: ShadowRoot;
  private iframe: HTMLIFrameElement | null = null;
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  // P3 fix: the embed origin the CURRENT iframe was opened against, kept
  // around (not just a local inside openTryOn()) so refreshTheme() below
  // can re-target the SAME theme postMessage channel without re-deriving
  // or re-validating the embed URL. `null` until openTryOn() actually
  // mounts an iframe.
  private embedOrigin: string | null = null;
  private isReady = false;
  private readonly readyPromise: Promise<void>;
  private resolveReady!: () => void;

  static get observedAttributes(): string[] {
    return [
      "tenant",
      "embed-url",
      "api-url",
      "api-key",
      "mock",
      "height",
      "max-width",
      "use-recommendation",
      "use-ai-advisor",
      "use-character",
      "sku",
      "ean",
      "auto-open",
    ];
  }

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
    // Latch for `vivian:ready`: the CustomEvent below fires once, synchronously,
    // during connectedCallback, so any listener registered after that instant
    // (e.g. host code that runs later than an async/deferred loader.js) misses
    // it permanently. `readyPromise` remembers whether that already happened,
    // so `whenReady()` (see below) always resolves correctly regardless of
    // when it's called.
    this.readyPromise = new Promise((resolve) => {
      this.resolveReady = resolve;
    });
    this.renderTrigger();
  }

  connectedCallback(): void {
    this.isReady = true;
    // F7.1: resolved HERE, not in the constructor — a custom element's
    // computed style (in particular, custom properties set via a page
    // STYLESHEET rule rather than inline `style=`) isn't reliably resolvable
    // until the element is actually connected to the document. `renderTrigger()`
    // itself already ran in the constructor and left the button with its
    // safe, hardcoded stylesheet default; this only ever UPGRADES it once a
    // valid, sufficiently-contrasting override is confirmed (and CLEARS it
    // back down when the current value no longer qualifies — see
    // `applyTriggerColorFromHost`'s own doc comment).
    this.applyTriggerColorFromHost();
    // P3 fix (pre-existing bug the F7.1 theme work made worth fixing):
    // `disconnectedCallback` below removes `this.messageHandler` from
    // `window` (WITHOUT clearing the field itself — see that method's own
    // comment) but nothing used to re-add it — so an element moved/
    // re-parented in the DOM (disconnectedCallback then connectedCallback,
    // e.g. a host page's own layout code re-appending it elsewhere)
    // silently stopped relaying EVERY iframe->host event (`frame-selected`
    // included) forever after, with no error, no console output, nothing.
    // `this.messageHandler` is `null` until `openTryOn()` first creates it
    // (the trigger hasn't been clicked yet) — this check is a harmless
    // no-op for that case, and exactly restores the relay on every
    // reconnect once it has been.
    if (this.messageHandler) {
      window.addEventListener("message", this.messageHandler);
    }
    this.resolveReady();
    // Página de produto: `<use-vivian auto-open>` monta o iframe na hora, sem
    // esperar o clique no botão. `openTryOn()` já é idempotente (re-conexão
    // não monta um segundo iframe).
    if (this.autoOpen) {
      this.openTryOn();
    }
    this.dispatchEvent(new CustomEvent(VIVIAN_EVENTS.ready, { bubbles: true, composed: true }));
  }

  /**
   * F7.1 (tema do lojista, entrega 2): the trigger button's background is
   * read straight into an inline style here — NOT expressed as
   * `var(--vivian-color-primary, #6b21ff)` in `renderTrigger()`'s
   * stylesheet — because a `var()` fallback only kicks in when the custom
   * property is UNSET, not when it's set to something invalid ("invalid at
   * computed-value time" per the CSS Custom Properties spec: the property
   * using `var()` falls back to ITS OWN initial/inherited value, not the
   * `var()` call's fallback argument). A host-declared `--vivian-color-primary:
   * not-a-color` would silently drop the button's background to transparent
   * — exactly the "controles ilegíveis" failure mode this task's
   * architecture gate flagged for the focus ring, just one level up. Reading,
   * validating, and only conditionally overriding via JS sidesteps that
   * gotcha entirely — the stylesheet's literal `#6b21ff` is the fallback,
   * full stop, never subject to CSS substitution semantics.
   *
   * Same accessibility gate as `@vivian/embed-app`'s `applyTheme.ts`
   * (`evaluateContrast`, shared from `@vivian/shared-contracts`) — see
   * this element's `WHITE`/`DEFAULT_COLOR_PRIMARY` constants' own comments.
   *
   * P1 fix: the button's TEXT color follows the same on-primary logic as
   * `applyTheme.ts`'s `--vivian-color-on-primary` (`pickOnColor`) — picked
   * by luminance instead of the hardcoded `color: #fff` this used to leave
   * untouched, so a themed background that reads better with dark text
   * (a saturated orange, say) doesn't end up with illegible white labels.
   *
   * P3 fixes (architecture-gate follow-up):
   * - CLEARS both `background`/`color` back to `""` on the invalid/absent
   *   path, rather than only ever setting and never clearing. Before this,
   *   a valid color applied once and later reconnecting (`disconnectedCallback`
   *   → `connectedCallback`, see that method's own comment) with no longer
   *   qualifying (removed or changed to something invalid) left the STALE
   *   inline style in place — this method is called fresh on every
   *   connect, so it must fully own both outcomes, not just the positive one.
   * - `console.warn`s on rejection, and — like `applyTheme.ts` — distinguishes
   *   "unrecognized" from "recognized but insufficient contrast" rather
   *   than collapsing both into silence.
   */
  private applyTriggerColorFromHost(): void {
    const button = this.shadow.querySelector("button") as HTMLButtonElement | null;
    if (!button) return;
    const raw = this.readCustomProperty("--vivian-color-primary");
    if (!raw) {
      button.style.background = "";
      button.style.color = "";
      return;
    }
    const evaluation = evaluateContrast(raw, WHITE);
    if (evaluation.ok) {
      button.style.background = raw;
      button.style.color = pickOnColor(raw);
      return;
    }
    button.style.background = "";
    button.style.color = "";
    if (evaluation.reason === "unrecognized") {
      // eslint-disable-next-line no-console
      console.warn(`[use-vivian] --vivian-color-primary não reconhecida: "${raw}". Usando o padrão.`);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`[use-vivian] --vivian-color-primary com contraste insuficiente: "${raw}". Usando o padrão.`);
    }
  }

  /**
   * P3 fix (architecture-gate follow-up, "o tema é lido uma única vez"):
   * `--vivian-color-primary`/`--vivian-radius`/`--vivian-font-family` are
   * otherwise only ever READ once — the trigger button's own copy in
   * `connectedCallback`, the iframe's copy at `embed-ready` time (see
   * `openTryOn()` below). A host page with something like a dark-mode
   * toggle that flips `--vivian-color-primary` via a stylesheet rule AFTER
   * either of those moments has no built-in way to propagate that change.
   * Public, idempotent, safe to call any number of times (including before
   * `openTryOn()` has ever run): re-reads this element's CURRENT computed
   * style and re-applies it to the trigger button locally, and — ONLY if
   * the iframe is already open — re-forwards it to the embed app by
   * reusing the EXACT SAME theme postMessage channel `openTryOn()` already
   * established (`this.embedOrigin`, the SAME iframe `contentWindow`); no
   * new listener, no new handshake round trip, which is what makes this
   * cheap enough to justify over leaving the limitation purely documented.
   *
   * Inherits `applyTheme.ts`'s own "additive, never explicitly clears a
   * PREVIOUSLY-valid custom property on the iframe side" contract for the
   * forwarded half of this call — see that module's doc comment. Concretely:
   * if a host flips from a valid theme to an explicitly invalid one and
   * calls this, the trigger button (this side) correctly clears itself
   * (see `applyTriggerColorFromHost` above), but the iframe's own
   * `document.documentElement` may still carry the PREVIOUS valid value,
   * since `applyTheme` was never designed to retract one. Documented, not
   * silently assumed — see `@vivian/embed-app`'s README, "Tema do lojista
   * (F7.1)" section (this package has no README of its own yet).
   */
  refreshTheme(): void {
    this.applyTriggerColorFromHost();
    if (this.iframe?.contentWindow && this.embedOrigin) {
      const theme = this.themeTokensToForward;
      if (Object.keys(theme).length > 0) {
        this.iframe.contentWindow.postMessage({ type: THEME_MESSAGE_TYPE, payload: theme }, this.embedOrigin);
      }
    }
  }

  /** Whether `vivian:ready` has already fired for this element instance. */
  get ready(): boolean {
    return this.isReady;
  }

  /**
   * Resolves once the element is ready (i.e. once `vivian:ready` has fired) —
   * immediately if it already is. Prefer this over listening for the
   * `vivian:ready` CustomEvent directly whenever the listener might be
   * registered after the element has connected to the DOM.
   */
  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  disconnectedCallback(): void {
    // P3 fix: deliberately does NOT null `this.messageHandler` — only
    // detaches it from `window`. `connectedCallback` re-attaches this SAME
    // handler reference on reconnect (see that method's own comment); if
    // this cleared the field, that check would always see `null` again
    // and the relay would never come back after a single disconnect. The
    // handler itself is stateless (a pure function of the MessageEvent it
    // receives, per `message-relay.ts`'s own doc comment), so keeping the
    // reference alive across a disconnect has no correctness cost.
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
    }
  }

  get tenant(): string {
    return this.getAttribute("tenant") ?? "";
  }

  get embedUrl(): string {
    return this.getAttribute("embed-url") ?? DEFAULT_EMBED_URL;
  }

  /** Base URL of the catalog API, forwarded to the embed app as the `api` query param. */
  get apiUrl(): string {
    return this.getAttribute("api-url") ?? "";
  }

  /**
   * The tenant's `PublicApiKey` (shown on the portal's /integracao page,
   * already baked into the copy-pasteable snippet — see
   * `WidgetSnippetBuilder.cs`), sent as the `X-Vivian-Public-Key` header on
   * every WRITE call (analytics ingestion, visagismo advice) — never on a
   * read. Optional: when absent, the embed simply never sends the header,
   * which is exactly what the zero-config (no-DB) API already expects (see
   * `embedParams.ts`/`apiKeyHeader.ts`) — nothing breaks, no exception, no
   * error screen.
   *
   * F7: this value NEVER travels in the iframe URL (it used to, as the
   * `apiKey` query param — an architecture-gate finding: a query string
   * lands in browser history, `Referer` headers on subrequests, and every
   * CDN/proxy/WAF access log in front of the embed domain, which is a much
   * worse blast radius than the optician's own page HTML). It's handed to
   * the iframe via `openTryOn()`'s postMessage reply to the embed app's
   * `embed-ready` handshake signal instead — see that method and
   * `message-relay.ts`'s `onEmbedReady`. Only a non-secret `hasApiKey=1`
   * boolean flag goes in the URL now, so the embed app knows whether to
   * expect a key at all (see `openTryOn()`'s own comment on that flag).
   */
  get apiKey(): string {
    return this.getAttribute("api-key") ?? "";
  }

  /** Presence-based boolean attribute: `<use-vivian mock>` or `mock=""` both mean "on". */
  get mock(): boolean {
    return this.hasAttribute("mock");
  }

  /**
   * F7.2 (capacidades desabilitáveis por atributo): `<use-vivian
   * use-recommendation="false">` turns off face-shape classification and
   * everything that depends on it — the "Seu rosto: …" chip, the catalog's
   * affinity-based reorder, and the recommendation badge (and, as a
   * consequence of the AI advisor requiring a locked face shape to open at
   * all, the AI-advisor consultation too — see `embedParams.ts`'s
   * `useRecommendation` doc comment). Try-on itself keeps working
   * unchanged: the shopper still tries frames on, just without any
   * visagismo consultoria attached.
   *
   * VALUE-based, deliberately NOT presence-based like {@link mock} above.
   * HTML's classic boolean-attribute convention (presence = on, VALUE
   * ignored — `disabled="false"` still disables) would make the one
   * spelling most integrators would actually type
   * (`use-recommendation="false"`) do the OPPOSITE of what it says, since
   * this capability already ships ON by default (there was no prior
   * "off" state to invert away from, unlike `disabled`). `aria-hidden` is
   * this platform's own precedent for an attribute whose VALUE — not just
   * presence — is what's actually read.
   *
   * Case-sensitive: only the exact literal "false" turns it off. "FALSE",
   * "False", "", absent, or any other value (including a typo like
   * "talvez") all keep it ON — a deliberate fail-open choice, so a lojista
   * who mistypes the value doesn't silently lose a capability they
   * presumably meant to keep armed.
   */
  get useRecommendation(): boolean {
    return this.getAttribute("use-recommendation") !== "false";
  }

  /**
   * `<use-vivian use-ai-advisor="false">` — suppresses the AI-advisor
   * consultation panel. ANDed, inside the embed app (`AdvisorPanel.tsx`),
   * with the backend's OWN `GET /api/v1/visagismo/capabilities` gate — two
   * independent vetoes, neither substitutes the other; either one saying
   * no is enough. Same value-based, case-sensitive contract as
   * {@link useRecommendation} above — see that getter's own doc comment
   * for the full rationale.
   */
  get useAiAdvisor(): boolean {
    return this.getAttribute("use-ai-advisor") !== "false";
  }

  /**
   * `<use-vivian use-character="false">` — turns off the Vivian character
   * entirely: no avatar, no speech bubble, anywhere (spec
   * docs/superpowers/specs/2026-09-14-personagem-chip-design.md). Every piece of
   * text the widget shows today stays exactly as it is; only her voice and face
   * disappear. Same value-based, case-sensitive contract as
   * {@link useRecommendation}/{@link useAiAdvisor} above — see the first of
   * those getters for the full rationale.
   */
  get useCharacter(): boolean {
    return this.getAttribute("use-character") !== "false";
  }

  /**
   * `<use-vivian sku="...">` / `ean="..."` — the product the shopper is
   * looking at on the host page. The embed app pre-selects the catalog frame
   * whose `sku` (the lojista's `SkuInterno`, set in the Portal) or EAN
   * matches, so the try-on opens already wearing that frame. Not a secret
   * (it's the page's own product code), so unlike `api-key` it travels in
   * the iframe URL. Read once, when the iframe mounts — a host that changes
   * product re-creates the element. A code with no match in the catalog is
   * harmless: the catalog opens with nothing selected, as before.
   */
  get sku(): string {
    return this.getAttribute("sku")?.trim() ?? "";
  }

  /** See {@link sku}. */
  get ean(): string {
    return this.getAttribute("ean")?.trim() ?? "";
  }

  /** Presence-based like {@link mock}: `<use-vivian auto-open>` mounts the iframe on connect, without the trigger button. */
  get autoOpen(): boolean {
    return this.hasAttribute("auto-open");
  }

  /**
   * `<use-vivian height="800">` (bare number = px) or `height="80vh"` —
   * overrides the iframe's default height ({@link DEFAULT_IFRAME_HEIGHT}).
   * An invalid value falls back to the default and logs a console error,
   * the same posture `api-url` already has for a malformed value.
   */
  get height(): string {
    const raw = this.getAttribute("height");
    if (!raw) return DEFAULT_IFRAME_HEIGHT;
    const parsed = parseCssLength(raw);
    if (!parsed) {
      // eslint-disable-next-line no-console
      console.error(`[use-vivian] height inválida: "${raw}". Usando o padrão (${DEFAULT_IFRAME_HEIGHT}).`);
      return DEFAULT_IFRAME_HEIGHT;
    }
    return parsed;
  }

  /**
   * `<use-vivian max-width="960">`/`max-width="60rem"` — overrides the host
   * element's default max-width ({@link DEFAULT_HOST_MAX_WIDTH}, unchanged
   * from before Fase 5). Same validation/fallback posture as {@link height}.
   */
  get maxWidth(): string {
    const raw = this.getAttribute("max-width");
    if (!raw) return DEFAULT_HOST_MAX_WIDTH;
    const parsed = parseCssLength(raw);
    if (!parsed) {
      // eslint-disable-next-line no-console
      console.error(`[use-vivian] max-width inválido: "${raw}". Usando o padrão (${DEFAULT_HOST_MAX_WIDTH}).`);
      return DEFAULT_HOST_MAX_WIDTH;
    }
    return parsed;
  }

  /**
   * Reads a CSS custom property declared on THIS element — via an inline
   * `style` attribute (`<use-vivian style="--vivian-width: 480px">`) or any
   * page stylesheet rule that targets it (`use-vivian { --vivian-width:
   * 480px; }`) — trimmed, `""` when absent. `getComputedStyle` (not
   * `this.style.getPropertyValue`) so BOTH declaration forms are picked up
   * uniformly, not just the inline-style one.
   *
   * F7.1: this is the read side of the "custom properties, not new HTML
   * attributes" decision for {@link width}/{@link iframeRadius}/theme
   * forwarding below — unlike `height`/`max-width` (pre-existing attributes,
   * left untouched), these are purely presentational values the
   * architecture gate already settled on CSS custom properties for (the
   * SAME mechanism that has to carry `--vivian-color-primary`/
   * `--vivian-font-family` to the iframe anyway, since those can't work as
   * plain attributes at all — an attribute can't be inherited/cascaded by
   * a stylesheet the way a custom property can). Adding a redundant
   * `width`/`radius` ATTRIBUTE alongside a custom property doing the exact
   * same job would just be two spellings of one knob, so there isn't one.
   */
  private readCustomProperty(name: string): string {
    return getComputedStyle(this).getPropertyValue(name).trim();
  }

  /**
   * `--vivian-width` (e.g. `<use-vivian style="--vivian-width: 480px">`) —
   * overrides the host element's width, hardcoded to {@link DEFAULT_HOST_WIDTH}
   * before this feature existed (see `openTryOn()`'s own comment on why it's
   * an inline style at all). Same validation/fallback posture as
   * {@link height}/{@link maxWidth}, just sourced from a custom property
   * instead of an attribute.
   */
  get width(): string {
    const raw = this.readCustomProperty("--vivian-width");
    if (!raw) return DEFAULT_HOST_WIDTH;
    const parsed = parseCssLength(raw);
    if (!parsed) {
      // eslint-disable-next-line no-console
      console.error(`[use-vivian] --vivian-width inválido: "${raw}". Usando o padrão (${DEFAULT_HOST_WIDTH}).`);
      return DEFAULT_HOST_WIDTH;
    }
    return parsed;
  }

  /**
   * `--vivian-radius` (e.g. `<use-vivian style="--vivian-radius: 4px">`) —
   * overrides the iframe's border-radius, hardcoded to
   * {@link DEFAULT_IFRAME_RADIUS} before this feature existed and, until
   * now, impossible for the host to reach at all (the iframe lives inside a
   * shadow root with no `part` exposed). Same validation/fallback posture as
   * {@link height}/{@link maxWidth}.
   */
  get iframeRadius(): string {
    const raw = this.readCustomProperty("--vivian-radius");
    if (!raw) return DEFAULT_IFRAME_RADIUS;
    const parsed = parseCssLength(raw);
    if (!parsed) {
      // eslint-disable-next-line no-console
      console.error(`[use-vivian] --vivian-radius inválido: "${raw}". Usando o padrão (${DEFAULT_IFRAME_RADIUS}).`);
      return DEFAULT_IFRAME_RADIUS;
    }
    return parsed;
  }

  /**
   * F7.1 (tema do lojista, entrega 2): the theme tokens worth forwarding to
   * the embed app, read RAW (unvalidated) from `<use-vivian>`'s own custom
   * properties — `@vivian/embed-app`'s `applyTheme.ts` independently
   * validates/sanitizes everything it receives (see `theme-handshake.test.ts`'s
   * top doc comment for why forwarding raw + validating twice, once per
   * side, is the deliberate design rather than trusting this side's own
   * local pass/fail). Only includes a key when the corresponding property
   * was actually declared — an empty result means "the host themed
   * nothing", which `openTryOn()` below turns into "send no theme message
   * at all", identical in spirit to how an absent `api-key` attribute means
   * no `api-key` reply is ever sent.
   *
   * devsecops-validator review (BAIXO fix): also drops a value whose length
   * exceeds `MAX_FONT_FAMILY_LENGTH` (shared from `@vivian/shared-contracts`,
   * NOT recomputed here — see that constant's own doc comment). Before this,
   * that ceiling was enforced ONLY on the RECEIVING side (`isSafeFontFamily`,
   * embed-app) and ONLY for `fontFamily` — `colorPrimary`/`radius` had no
   * cap at all on the SEND side, so an absurdly large custom-property value
   * would be serialized whole and posted across the iframe boundary before
   * ever being validated/discarded there. Confirmed no ReDoS risk in the
   * receiving side's own parsers (anchored regexes, ~38ms even on 10MB
   * input) — this is bandwidth hygiene, not a vulnerability fix: no reason
   * to let a value the other side will reject anyway travel the wire at
   * full size first.
   */
  private get themeTokensToForward(): ThemeTokens {
    const tokens: ThemeTokens = {};
    const colorPrimary = this.readCustomProperty("--vivian-color-primary");
    if (colorPrimary && colorPrimary.length <= MAX_FONT_FAMILY_LENGTH) tokens.colorPrimary = colorPrimary;
    const radius = this.readCustomProperty("--vivian-radius");
    if (radius && radius.length <= MAX_FONT_FAMILY_LENGTH) tokens.radius = radius;
    const fontFamily = this.readCustomProperty("--vivian-font-family");
    if (fontFamily && fontFamily.length <= MAX_FONT_FAMILY_LENGTH) tokens.fontFamily = fontFamily;
    return tokens;
  }

  private renderTrigger(): void {
    const style = document.createElement("style");
    style.textContent = `
      /* "all: initial" resets inherited/host page styles; "display" is set
         explicitly afterwards because its own initial value is "inline",
         which mishandles a block-level trigger button. Sizing beyond this
         (host width/max-width once the iframe mounts, the iframe's own
         width/height) is set as inline styles directly on the elements in
         openTryOn() below, rather than here — see the comment there for why. */
      :host { all: initial; display: inline-block; }
      button {
        font: inherit;
        cursor: pointer;
        border-radius: 999px;
        border: none;
        padding: 0.75em 1.5em;
        background: ${DEFAULT_COLOR_PRIMARY};
        color: #fff;
      }
    `;

    const button = document.createElement("button");
    button.type = "button";
    button.textContent = "Experimentar óculos ✨";
    button.addEventListener("click", () => this.openTryOn());

    this.shadow.append(style, button);
  }

  private openTryOn(): void {
    if (this.iframe) {
      return;
    }
    if (!this.tenant) {
      // eslint-disable-next-line no-console
      console.error('[use-vivian] o atributo "tenant" é obrigatório.');
      return;
    }

    const embedUrl = new URL(this.embedUrl);
    if (!isAllowedEmbedOrigin(embedUrl)) {
      // eslint-disable-next-line no-console
      console.error(`[use-vivian] embed-url de origem não permitida: "${embedUrl.origin}".`);
      return;
    }
    embedUrl.searchParams.set("tenant", this.tenant);
    // The embed app runs in a cross-origin iframe, so it cannot read
    // window.parent.location itself; it needs parentOrigin explicitly to
    // know the postMessage targetOrigin to use when talking back to us.
    embedUrl.searchParams.set("parentOrigin", window.location.origin);
    if (this.apiUrl) {
      let apiUrl: URL | null = null;
      try {
        apiUrl = new URL(this.apiUrl);
      } catch {
        // eslint-disable-next-line no-console
        console.error(`[use-vivian] api-url inválida: "${this.apiUrl}".`);
      }
      if (apiUrl) {
        if (isAllowedApiOrigin(apiUrl)) {
          embedUrl.searchParams.set("api", this.apiUrl);
        } else {
          // eslint-disable-next-line no-console
          console.error(`[use-vivian] api-url de origem não permitida: "${apiUrl.origin}".`);
        }
      }
    }
    if (this.mock) {
      embedUrl.searchParams.set("mock", "1");
    }
    // F7.2 (capacidades desabilitáveis por atributo): only sent when OFF —
    // default is ON, matching the "absence means default" convention every
    // other optional param here already follows (mock/hasApiKey/
    // debugshape above/below). The literal forwarded is "false" itself
    // (not a numeric 0/1 flag) — the SAME semantic marker
    // `embedParams.ts`'s `parseCapabilityFlag` reads back on the other
    // side, so there's exactly one decision point ("false", case-sensitive)
    // for on/off, not two encodings of the same thing.
    if (!this.useRecommendation) {
      embedUrl.searchParams.set("useRecommendation", "false");
    }
    if (!this.useAiAdvisor) {
      embedUrl.searchParams.set("useAiAdvisor", "false");
    }
    if (!this.useCharacter) {
      embedUrl.searchParams.set("useCharacter", "false");
    }
    // Pre-selected product (see the `sku` getter) — only sent when set,
    // same "absence means default" convention as the flags above.
    if (this.sku) {
      embedUrl.searchParams.set("sku", this.sku);
    }
    if (this.ean) {
      embedUrl.searchParams.set("ean", this.ean);
    }
    // F7: the api-key VALUE never goes in the URL (see the `apiKey` getter's
    // doc comment) — only this non-secret boolean, so the embed app knows
    // whether to hold its first analytics flush for a key that's actually
    // coming (see `embedParams.ts`'s `apiKeyExpected` and
    // `analyticsService.ts`'s handshake-gated flush) instead of waiting
    // forever for a key that was never going to arrive.
    if (this.apiKey) {
      embedUrl.searchParams.set("hasApiKey", "1");
    }

    const iframe = document.createElement("iframe");
    iframe.setAttribute("allow", "camera");
    iframe.setAttribute("sandbox", "allow-scripts allow-same-origin");
    iframe.src = embedUrl.toString();
    // Useful default size. An unstyled <iframe> whose containing block has
    // no definite size falls back to the HTML spec's default replaced-element
    // box (300x150 CSS px) — and that's exactly what happened here: `:host`
    // was `display: inline-block` with an auto (shrink-to-fit) width, so a
    // percentage-sized iframe inside it had nothing definite to resolve
    // against. The Fase 1 pilot (05/08/2026) saw this as the camera video
    // squeezed into a ~200px-tall strip with a scrollbar next to it. Setting
    // real inline styles directly on both elements (highest cascade
    // priority — can't be silently shadowed by a host page's own CSS) fixes
    // both ends: the host becomes a sized block box, and the iframe gets an
    // explicit height instead of an unresolvable percentage.
    iframe.style.display = "block";
    iframe.style.borderWidth = "0";
    iframe.style.width = "100%";
    iframe.style.height = this.height;
    // F7.1: was hardcoded "1rem" — the iframe lives inside a shadow root
    // with no `part` exposed, so this was impossible for the host to
    // override at all before {@link iframeRadius} (see that getter's own
    // comment). Falls back to the EXACT same "1rem" when nothing/nothing
    // valid is declared.
    iframe.style.borderRadius = this.iframeRadius;

    this.style.display = "block";
    // F7.1: was hardcoded "100%" — see {@link width}'s own comment. Falls
    // back to the EXACT same "100%" when nothing/nothing valid is declared,
    // so an already-embedded `<use-vivian>` with no `--vivian-width` set
    // renders byte-for-byte the same as before this feature existed.
    this.style.width = this.width;
    this.style.maxWidth = this.maxWidth;

    this.shadow.append(iframe);
    this.iframe = iframe;
    // auto-open: there was no click, so the trigger would just sit above the
    // already-open try-on doing nothing — hide it.
    if (this.autoOpen) {
      const button = this.shadow.querySelector("button");
      if (button) button.hidden = true;
    }
    // P3 fix: remembered beyond this method's own local `embedUrl` so
    // `refreshTheme()` can re-target the SAME origin later without
    // re-deriving/re-validating it — see that method's own doc comment.
    this.embedOrigin = embedUrl.origin;

    // contentWindow only exists once the iframe is attached to the document,
    // so the relay must be wired up after the append above.
    //
    // F7 apiKey handshake reply: fires once per validated `embed-ready`
    // message from the iframe (see `message-relay.ts`'s `onEmbedReady` doc
    // comment — not one-shot). Only ever sends when `this.apiKey` is
    // actually set; a host that never configured one has nothing to reply
    // with, matching `hasApiKey`'s absence above and requiring no special
    // handling on the embed side beyond "never expected a key" (see
    // `embedParams.ts`). Targeted at `embedUrl.origin` exactly — the SAME
    // origin `message-relay.ts` already validates incoming messages
    // against, never `"*"`.
    this.messageHandler = createMessageRelay(
      embedUrl.origin,
      iframe.contentWindow,
      (relayedEvent) => {
        this.dispatchEvent(relayedEvent);
      },
      () => {
        if (this.apiKey && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            { type: API_KEY_MESSAGE_TYPE, payload: { apiKey: this.apiKey } },
            embedUrl.origin,
          );
        }
        // F7.1 theme handshake reply: reuses this SAME `embed-ready`
        // signal/reply slot rather than standing up a second one (see this
        // file's `themeTokensToForward` doc comment). Only sent when at
        // least one token was actually declared — an optician who hasn't
        // themed anything sees no new postMessage traffic at all, matching
        // `api-key`'s own "absence sends nothing" posture above.
        const theme = this.themeTokensToForward;
        if (iframe.contentWindow && Object.keys(theme).length > 0) {
          iframe.contentWindow.postMessage({ type: THEME_MESSAGE_TYPE, payload: theme }, embedUrl.origin);
        }
      },
    );
    window.addEventListener("message", this.messageHandler);
  }
}
